import { motion } from "framer-motion";
import type React from "react";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

interface BottomNavItemProps
  extends Omit<React.ComponentProps<typeof Link>, "href"> {
  href: string;
  icon: React.ComponentType<{ variant: "Bold" | "Linear"; className?: string }>;
  label: string;
  isActive: boolean;
  className?: string;
}

export const BottomNavItem: React.FC<BottomNavItemProps> = ({
  href,
  icon: Icon,
  label,
  isActive,
  className,
  ...props
}) => {
  return (
    <Link
      {...props}
      href={href}
      className={cn(
        // Equal-width items so every active circle sits at a predictable
        // center that lines up with the notch cutout (labels no longer shift
        // the icons).
        "flex flex-1 basis-0 flex-col items-center justify-center",
        "pb-[12px] font-medium",
        "group transition-all duration-500 ease-in-out",
        isActive ? "text-accent" : "text-foreground",
        className,
      )}
    >
      <motion.span
        className={cn(
          "flex size-[56px] items-center justify-center",
          "rounded-full bg-transparent",
          "transition-all duration-500 ease-in-out motion-reduce:transition-none",
          isActive ? "mb-[20px] bg-bg-secondary" : "-mb-[6px]",
        )}
      >
        {
          <Icon
            variant={isActive ? "Bold" : "Linear"}
            className="size-[24px]"
          />
        }
      </motion.span>
      <span className="max-w-full truncate text-[11px] leading-[14px]">
        {label}
      </span>
    </Link>
  );
};
